import { Box, Heading, Button, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { ApiMessageStack, NavigationPrompt } from "components";
import { LOGIN_ROUTE, SIGNUP_ROUTE } from "constants/routes";
import { useLocation } from "react-router-dom";
import { useAppStore } from "utils/react-hooks/useAppStore.hook";
import { observer } from "mobx-react";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { DEFAULT_ERROR_TOAST_OPTIONS } from "constants/default-toast-options";
import { useTurnstileV2 } from "utils/react-hooks/useTurnstileV2.hook";
import { useToken } from "utils/react-hooks/useToken.hook";

export const UserOnboard = observer(() => {
  const [status, setStatus] = useState<ApiRequestStatusEnum | null>(null);
  const [tokenIsValid, setTokenIsValid] = useState(true);
  const toast = useToast();
  const { turnstileToken, resetTurnstile } = useTurnstileV2();
  const {
    authStore: {
      leadProAuthStore: { signup, verifySignupToken },
    },
  } = useAppStore();
  const location = useLocation();
  const token = useToken();

  const onSubmit = useCallback(async () => {
    try {
      if (!!turnstileToken) {
        setStatus(ApiRequestStatusEnum.PENDING);
        await signup(token, turnstileToken);
        setStatus(ApiRequestStatusEnum.SUCCESS);
      }
    } catch (e) {
      toast({
        ...DEFAULT_ERROR_TOAST_OPTIONS,
        description: <ApiMessageStack messageStack={e.message} />,
      });
      resetTurnstile();
    }
  }, [token, toast, turnstileToken, signup, resetTurnstile]);

  const renderMissingToken = () => {
    return (
      <Box>
        <Box mb={4}>
          No user with customer's email is currently registered on the LeadPro
          dashboard.
        </Box>
      </Box>
    );
  };

  const renderInvalidToken = () => {
    return (
      <NavigationPrompt
        text={`Your account creation has expired.\nPlease ask the customer to register their account again and verify their email within 4 weeks of creating their account.`}
        ctaText={"Sign up again."}
        ctaNavigateTo={SIGNUP_ROUTE}
      />
    );
  };

  const renderValidToken = useCallback(() => {
    if (status !== ApiRequestStatusEnum.SUCCESS) {
      return (
        <>
          <Button
            type={"submit"}
            width={"full"}
            colorScheme={"blue"}
            onClick={onSubmit}
            isLoading={status === ApiRequestStatusEnum.PENDING}
          >
            Activate account
          </Button>
        </>
      );
    }

    return (
      <NavigationPrompt
        text={
          "The customer's account has been successfully activated and they should receive an email shortly. You can now close this page."
        }
        ctaText={"Go to login"}
        ctaNavigateTo={LOGIN_ROUTE}
      />
    );
  }, [status, onSubmit]);

  useEffect(() => {
    const checkToken = async () => {
      try {
        await verifySignupToken(token);
        setTokenIsValid(true);
      } catch (e) {
        setTokenIsValid(false);
      }
    };

    if (token) {
      checkToken();
    }
  }, [verifySignupToken, location, token]);

  return (
    <Box maxW="300px" width="100%">
      <Heading fontSize="2xl" textAlign="center" lineHeight="none" mb={6}>
        Activate customer's account
      </Heading>
      {!!token
        ? tokenIsValid
          ? renderValidToken()
          : renderInvalidToken()
        : renderMissingToken()}
    </Box>
  );
});
